import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  GraduationCap,
  Mail,
  MapPin,
  Menu,
  Phone,
  Play,
  Quote,
  Search,
  Sparkles,
  Users,
  X,
  FileText,
  Newspaper,
  FlaskConical,
  Landmark,
  HeartHandshake,
  Trophy,
  Video,
} from "lucide-react";


/* =========================================================
   CONFIGURATION
========================================================= */

const FACULTY_DEPARTMENT_ID = "5";
const NOTICE_DEPARTMENT_ID = "5";

const CONTENT_DEPARTMENT_ID = "5";
const PROGRAMME_ID = "5"; // Verify against the programme ID configured in your API.


/* =========================================================
   TABS
========================================================= */

const tabs = [
  { id: "overview", label: "Overview", icon: Landmark },
  { id: "chairman", label: "Chairman Message", icon: Quote },
  { id: "fees", label: "Tuition Fees", icon: DollarSign },
  { id: "notice", label: "Notice", icon: FileText },
  { id: "news", label: "News", icon: Newspaper },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "videos", label: "Videos", icon: Video },
  { id: "aid", label: "Financial Aid", icon: HeartHandshake },
  { id: "faculty", label: "Faculty Members", icon: Users },
  { id: "club", label: "HTM Club", icon: Trophy },
  { id: "research", label: "Research", icon: FlaskConical },
  { id: "facilities", label: "Facilities", icon: Building2 },
  { id: "curriculum", label: "Course Curriculum", icon: BookOpen },
  { id: "offered", label: "Course Offered", icon: GraduationCap },
  { id: "contact", label: "Contact Us", icon: MapPin },
];


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function HTMDepartment() {

  const [activeTab, setActiveTab] = useState("overview");
  const [mobileMenu, setMobileMenu] = useState(false);

  const [faculty, setFaculty] = useState([]);
  const [notices, setNotices] = useState([]);
  const [news, setNews] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseOffered, setCourseOffered] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [autoPlay, setAutoPlay] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingC, setLoadingC] = useState(false);

  const [error, setError] = useState(null);


  /* =========================================================
     AXIOS INSTANCE
  ========================================================= */

  const api = useMemo(() => {
    return axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }, []);


  /* =========================================================
     FACULTY API
  ========================================================= */

  const facultyPost = async () => {

    setLoading(true);
    setError(null);

    try {

      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        department_id: FACULTY_DEPARTMENT_ID,
        designation_id: "0",
      };

      const response = await api.post(
        "/teacher-list",
        payload
      );

      setFaculty(response.data || []);

    } catch (err) {

      setError(
        err.response?.data?.message ||
        err.message ||
        "Unable to load faculty information."
      );

    } finally {
      setLoading(false);
    }
  };


  /* =========================================================
     NOTICE API
  ========================================================= */

  const noticePost = async () => {

    setLoading(true);
    setError(null);

    try {

      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        department_id: NOTICE_DEPARTMENT_ID,
      };

      const response = await api.post(
        "/recent-notice",
        payload
      );

      setNotices(response.data || []);

    } catch (err) {

      setError(
        err.response?.data?.message ||
        err.message ||
        "Unable to load notices."
      );

    } finally {
      setLoading(false);
    }
  };


  /* =========================================================
     NEWS API
  ========================================================= */

  const newsPost = async () => {

    setLoading(true);
    setError(null);

    try {

      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        department_id: CONTENT_DEPARTMENT_ID,
      };

      const response = await api.post(
        "/recent-news",
        payload
      );

      const data = response.data || [];

      setNews(data.slice(0, 48));

    } catch (err) {

      setError(
        err.response?.data?.message ||
        err.message ||
        "Unable to load news."
      );

    } finally {
      setLoading(false);
    }
  };


  /* =========================================================
     COURSE CURRICULUM API
  ========================================================= */

	const coursePost = async () => {
	  setLoadingCourses(true);
	  setError(null);

	  try {
		const payload = {
		  userId: import.meta.env.VITE_API_USER,
		  password: import.meta.env.VITE_API_PASS,
		  department_id: CONTENT_DEPARTMENT_ID,
		  programme_id: PROGRAMME_ID,
		};

		const response = await api.post(
		  "/program-syllabus",
		  payload
		);

		console.log("Curriculum Response:", response.data);

		setCourses(response.data || {});

	  } catch (err) {

		setError(
		  err.response?.data?.message ||
		  err.message ||
		  "Unable to load curriculum."
		);

	  } finally {
		setLoadingCourses(false);
	  }
	};


  /* =========================================================
     COURSE OFFERED API
  ========================================================= */

	const offerCourse = async () => {
	  setLoadingC(true);
	  setError(null);

	  try {

		const payload = {
		  userId: import.meta.env.VITE_API_USER,
		  password: import.meta.env.VITE_API_PASS,
		  department_id: CONTENT_DEPARTMENT_ID,
		  programme_id: PROGRAMME_ID,
		};

		const response = await api.post(
		  "/course-offered",
		  payload
		);

		console.log("Course Offered Response:", response.data);

		setCourseOffered(response.data || {});

	  } catch (err) {

		setError(
		  err.response?.data?.message ||
		  err.message ||
		  "Unable to load offered courses."
		);

	  } finally {
		setLoadingC(false);
	  }
	};
	const eventPost = async () => {

	  setLoadingEvents(true);
	  setError(null);

	  try {

		const payload = {
		  userId: import.meta.env.VITE_API_USER,
		  password: import.meta.env.VITE_API_PASS,
		  department_id: CONTENT_DEPARTMENT_ID,
		};

		const response = await api.post(
		  "/recent-events",
		  payload
		);

		console.log("Events Response:", response.data);

		setEvents(response.data || []);

	  } catch (err) {

		setError(
		  err.response?.data?.message ||
		  err.message ||
		  "Unable to load events."
		);

	  } finally {

		setLoadingEvents(false);

	  }
	};
	
	const videoPost = async () => {
	  setLoadingVideos(true);
	  setError(null);

	  try {

		const payload = {
		  userId: import.meta.env.VITE_API_USER,
		  password: import.meta.env.VITE_API_PASS,
		  department_id: "5",
		};

		const api = axios.create({
		  baseURL: import.meta.env.VITE_API_BASE_URL,
		  headers: {
			"Content-Type": "application/json",
		  },
		});

		const response = await api.post(
		  "/recent-videos",
		  payload
		);

		const data = Array.isArray(response.data)
		  ? response.data
		  : [];

		console.log("Videos Response:", data);

		setVideos(data);

		if (data.length > 0) {

		  setSelectedVideo(data[0]);

		  setAutoPlay(false);

		} else {

		  setSelectedVideo(null);

		}

	  } catch (err) {

		setError(
		  err.response?.data?.message ||
		  err.message ||
		  "Unable to load videos."
		);

	  } finally {

		setLoadingVideos(false);

	  }
	};

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {

    document.title =
      "Department of Hospitality & Tourism Management | Bandarban University";

    facultyPost();
    noticePost();
    newsPost();
    coursePost();
    offerCourse();
	eventPost();
	videoPost();
  }, []);


  /* =========================================================
     TAB CHANGE
  ========================================================= */

  const changeTab = (tab) => {

    setActiveTab(tab);
    setMobileMenu(false);

    setTimeout(() => {
      document
        .getElementById("department-content")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }, 50);
  };


  return (

    <div className="min-h-screen bg-[#f6faf7] text-slate-800">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="relative min-h-[92vh] overflow-hidden bg-[#063d24]">

        {/* Background Effects */}

        <div className="absolute inset-0">

          <div className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full bg-[#d99b16]/20 blur-3xl" />

          <div className="absolute right-[-150px] top-[15%] h-[550px] w-[550px] rounded-full bg-[#20a45a]/20 blur-3xl" />

          <div className="absolute bottom-[-200px] left-[30%] h-[500px] w-[500px] rounded-full bg-[#d99b16]/10 blur-3xl" />

          <motion.div
            animate={{
              rotate: [0, 360],
            }}
            transition={{
              duration: 40,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute right-[8%] top-[12%] h-72 w-72 rounded-full border border-white/10"
          />

          <motion.div
            animate={{
              rotate: [360, 0],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute right-[11%] top-[16%] h-56 w-56 rounded-full border border-[#d99b16]/20"
          />

        </div>

        {/* Hero Content */}

        <div className="relative z-10 mx-auto flex min-h-[82vh] max-w-7xl items-center px-5 py-16">

          <div className="grid w-full items-center gap-14 lg:grid-cols-[1.15fr_.85fr]">

            {/* Left */}

            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#d99b16]/30 bg-white/10 px-4 py-2 text-sm font-medium text-[#f5c95d] backdrop-blur-md">

                <Sparkles size={16} />

               School of Hospitality & Tourism Management

              </div>


              <h1 className="max-w-4xl text-4xl font-extrabold leading-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">

                Hospitality & Tourism Management

                <span className="block text-[#e8b637]">
                  
                </span>

              </h1>


              <p className="mt-7 max-w-2xl text-lg leading-8 text-white/75 md:text-xl">

                Preparing future hospitality and tourism professionals through quality education in hotel operations, tourism management, destination development, service excellence and sustainable practices.

              </p>


              <div className="mt-9 flex flex-wrap gap-4">

                <button
                  onClick={() => changeTab("overview")}
                  className="group flex items-center gap-3 rounded-xl bg-[#d99b16] px-6 py-3.5 font-bold text-white shadow-lg shadow-black/20 transition hover:-translate-y-1 hover:bg-[#e7ad2e]"
                >

                  Explore Department

                  <ArrowRight
                    size={18}
                    className="transition group-hover:translate-x-1"
                  />

                </button>


                <button
                  onClick={() => changeTab("curriculum")}
                  className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-6 py-3.5 font-semibold text-white backdrop-blur-md transition hover:bg-white/20"
                >

                  <BookOpen size={18} />

                  View Curriculum

                </button>

              </div>


              {/* Stats */}

              <div className="mt-12 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">

                <Stat
                  number="4"
                  label="Years"
                />

                <Stat
                  number="141"
                  label="Credits"
                />

                <Stat
                  number="HOTEL"
                  label="Hospitality"
                />

                <Stat
                  number="TRAVEL"
                  label="Tourism"
                />

              </div>

            </motion.div>


            {/* Right Logo */}

            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{
                duration: 1,
                delay: 0.2,
              }}
              className="flex justify-center lg:justify-end"
            >

              <div className="relative">

                {/* Glow */}

                <div className="absolute inset-0 scale-90 rounded-full bg-[#d99b16]/30 blur-3xl" />


                {/* Logo Card */}

                <motion.div
                  animate={{
                    y: [0, -12, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative flex h-[320px] w-[320px] items-center justify-center rounded-[3rem] border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-xl sm:h-[390px] sm:w-[390px]"
                >

                  <div className="absolute inset-5 rounded-[2.5rem] border border-[#d99b16]/30" />

                  <img
                    src="/assets/img/bubbanlogo.jpg"
                    alt="Bandarban University"
                    className="relative z-10 h-64 w-64 rounded-3xl object-contain shadow-xl sm:h-80 sm:w-80"
                  />

                </motion.div>

              </div>

            </motion.div>

          </div>

        </div>


        {/* Bottom Wave */}

        <div className="absolute bottom-0 left-0 right-0">

          <svg
            viewBox="0 0 1440 120"
            className="block h-auto w-full"
            preserveAspectRatio="none"
          >
            <path
              fill="#f6faf7"
              d="M0,64 C240,120 480,10 720,62 C960,114 1200,22 1440,64 L1440,120 L0,120 Z"
            />
          </svg>

        </div>

      </section>


      {/* =====================================================
          TAB NAVIGATION
      ===================================================== */}

      <section className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-xl">

        <div className="mx-auto max-w-7xl">

          {/* Mobile */}

          <div className="flex items-center justify-between px-5 py-3 lg:hidden">

            <div className="font-bold text-[#07552e]">
              Hospitality & Tourism Management Department
            </div>

            <button
              onClick={() => setMobileMenu(!mobileMenu)}
              className="rounded-lg bg-[#07552e] p-2 text-white"
            >
              {mobileMenu ? <X size={21} /> : <Menu size={21} />}
            </button>

          </div>


        
		<div className="hidden lg:block">

		  <div className="mx-auto flex max-w-7xl flex-wrap items-stretch justify-center gap-2 px-4 py-3">

			{tabs.map((tab) => {

			  const Icon = tab.icon;
			  const active = activeTab === tab.id;

			  return (

				<button
				  key={tab.id}
				  onClick={() => changeTab(tab.id)}
				  className={`
					group
					relative
					flex
					min-h-[48px]
					items-center
					justify-center
					gap-2
					rounded-xl
					px-4
					py-2.5
					text-sm
					font-semibold
					transition-all
					duration-300
					ease-out

					${
					  active
						? "bg-[#07552e] text-white shadow-md shadow-[#07552e]/20"
						: "bg-slate-50 text-slate-600 hover:bg-green-50 hover:text-[#07552e]"
					}
				  `}
				>

				  <Icon
					size={16}
					className={`
					  shrink-0 transition-transform duration-300
					  ${
						active
						  ? "text-[#e8b637]"
						  : "text-slate-400 group-hover:text-[#07552e]"
					  }
					`}
				  />

				  <span className="whitespace-nowrap">
					{tab.label}
				  </span>


				  {/* Active indicator */}

				  {active && (

					<motion.span
					  layoutId="activeTabIndicator"
					  className="absolute -bottom-1 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full bg-[#d99b16]"
					  transition={{
						type: "spring",
						stiffness: 500,
						damping: 30,
					  }}
					/>

				  )}

				</button>

			  );

			})}

		  </div>

		</div>


          {/* Mobile Menu */}

          <AnimatePresence>

            {mobileMenu && (

              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden border-t border-slate-100 lg:hidden"
              >

                <div className="max-h-[70vh] overflow-y-auto p-3">

                  {tabs.map((tab) => {

                    const Icon = tab.icon;

                    return (

                      <button
                        key={tab.id}
                        onClick={() => changeTab(tab.id)}
                        className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-semibold ${
                          activeTab === tab.id
                            ? "bg-[#07552e] text-white"
                            : "text-slate-600 hover:bg-green-50"
                        }`}
                      >

                        <Icon size={17} />

                        {tab.label}

                      </button>

                    );

                  })}

                </div>

              </motion.div>

            )}

          </AnimatePresence>

        </div>

      </section>


      {/* =====================================================
          CONTENT
      ===================================================== */}

      <main
        id="department-content"
        className="mx-auto max-w-7xl px-5 py-12 md:py-16"
      >

        <AnimatePresence mode="wait">

          <motion.div
            key={activeTab}
            initial={{
              opacity: 0,
              y: 15,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: -10,
            }}
            transition={{
              duration: 0.3,
            }}
          >

            {activeTab === "overview" && (
              <Overview />
            )}

            {activeTab === "chairman" && (
              <ChairmanMessage />
            )}

            {activeTab === "fees" && (
              <TuitionFees />
            )}

            {activeTab === "notice" && (
              <NoticeSection
                notices={notices}
                loading={loading}
              />
            )}

            {activeTab === "news" && (
              <NewsSection
                news={news}
                loading={loading}
              />
            )}

            {activeTab === "events" && (
			  <EventsSection
				events={events}
				loading={loadingEvents}
			  />
			)}

            {activeTab === "videos" && (
			  <VideosSection
				videos={videos}
				selectedVideo={selectedVideo}
				setSelectedVideo={setSelectedVideo}
				autoPlay={autoPlay}
				setAutoPlay={setAutoPlay}
				loading={loadingVideos}
			  />
			)}

            {activeTab === "aid" && (
              <FinancialAid />
            )}

            {activeTab === "faculty" && (
              <FacultySection
                faculty={faculty}
                loading={loading}
              />
            )}

            {activeTab === "club" && (
              <HTMClub />
            )}

            {activeTab === "research" && (
              <Research />
            )}

            {activeTab === "facilities" && (
              <Facilities />
            )}

            {activeTab === "curriculum" && (
              <Curriculum
                courses={courses}
                loading={loadingCourses}
              />
            )}

            {activeTab === "offered" && (
              <CourseOffered
                courses={courseOffered}
                loading={loadingC}
              />
            )}

            {activeTab === "contact" && (
              <Contact />
            )}

          </motion.div>

        </AnimatePresence>

      </main>


     

    </div>
  );
}


/* =========================================================
   STAT
========================================================= */

function Stat({ number, label }) {

  return (

    <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-md">

      <div className="text-2xl font-extrabold text-[#f2c453]">
        {number}
      </div>

      <div className="mt-1 text-xs text-white/60">
        {label}
      </div>

    </div>

  );
}


/* =========================================================
   SECTION HEADER
========================================================= */

function SectionHeader({
  eyebrow,
  title,
  description,
}) {

  return (

    <div className="mb-10">

      <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[#b47c00]">

        <span className="h-1 w-8 rounded-full bg-[#d99b16]" />

        {eyebrow}

      </div>

      <h2 className="text-3xl font-extrabold text-[#064b29] md:text-4xl">

        {title}

      </h2>

      {description && (

        <p className="mt-4 max-w-3xl leading-7 text-slate-600">

          {description}

        </p>

      )}

    </div>

  );
}


/* =========================================================
   OVERVIEW
========================================================= */

function Overview() {

  return (

    <div>

      <SectionHeader
        eyebrow="Welcome"
        title="Department Overview"
        description="Building knowledgeable, ethical and socially responsible graduates for a changing world."
      />


      <div className="grid gap-8 lg:grid-cols-5">

        <div className="rounded-3xl bg-white p-7 shadow-sm ring-1 ring-slate-100 lg:col-span-3">

          <h3 className="text-2xl font-bold text-[#07552e]">
            Hospitality & Tourism Management
          </h3>

          <p className="mt-5 leading-8 text-slate-600">

            The Department of Hospitality & Tourism Management provides a learning environment focused on hospitality operations, tourism planning, destination management, customer service, event management and sustainable tourism.

          </p>

          <p className="mt-4 leading-8 text-slate-600">

            The programme aims to develop competent and service-oriented hospitality and tourism graduates with analytical, communication, leadership, decision-making and problem-solving skills for careers in hotels, resorts, travel agencies, tour operations, event management, tourism development and academia.

          </p>


          <div className="mt-8 grid gap-4 sm:grid-cols-2">

            {[
              "Hospitality Management & Operations",
              "Tourism Marketing & Communication",
              "Hospitality Accounting & Financial Management",
              "Tourism Entrepreneurship & Strategy",
            ].map((item) => (

              <div
                key={item}
                className="flex items-center gap-3 rounded-xl bg-green-50 p-4"
              >

                <CheckCircle2
                  size={20}
                  className="text-[#08733b]"
                />

                <span className="font-medium text-slate-700">
                  {item}
                </span>

              </div>

            ))}

          </div>

        </div>


        <div className="rounded-3xl bg-gradient-to-br from-[#07552e] to-[#063d24] p-8 text-white shadow-xl lg:col-span-2">

          <GraduationCap size={42} className="text-[#e8b637]" />

          <h3 className="mt-6 text-2xl font-bold">
            Hospitality & Tourism Management Programme
          </h3>

          <p className="mt-2 text-lg text-[#e8b637]">
            Hospitality & Tourism Management
          </p>


          <div className="mt-8 space-y-5">

            <InfoRow
              icon={Clock}
              title="Duration"
              value="4 Years"
            />

            <InfoRow
              icon={BookOpen}
              title="Total Credits"
              value="141"
            />

            <InfoRow
              icon={Users}
              title="Core Credits"
              value="Hospitality & Tourism Core Courses"
            />

            <InfoRow
              icon={Trophy}
              title="Program"
              value="Undergraduate"
            />

          </div>

        </div>

      </div>

    </div>

  );
}


/* =========================================================
   CHAIRMAN
========================================================= */

function ChairmanMessage() {

  return (

    <div>

      <SectionHeader
        eyebrow="Leadership"
        title="Message from the Chairman"
      />


      <div className="grid items-center gap-10 lg:grid-cols-[.75fr_1.25fr]">

        <div className="relative">
		  {/* Background decoration */}
		  <div className="absolute -inset-4 rounded-[2rem] bg-[#d99b16]/10" />

		  {/* Main Card */}
		  <div className="relative overflow-hidden rounded-[2rem] bg-white p-3 shadow-xl">
			{/* Chairman Image */}
			<div className="overflow-hidden rounded-[1.5rem] bg-gradient-to-br from-green-50 to-amber-50">
			  <img
				src="/assets/img/bubbanlogo.jpg"
				alt="Department of Hospitality & Tourism Management Chairman"
				className="h-[350px] w-full object-contain p-8"
			  />
			</div>

			{/* Chairman Information */}
			<div className="px-4 py-6 text-center">
			  <h3 className="text-2xl font-bold text-[#07552e]">
				Department Chairman
			  </h3>

			  <p className="mt-2 font-semibold text-[#d99b16]">
				Chairman, Department of Hospitality & Tourism Management
			  </p>

			  <p className="mt-1 text-sm text-gray-600">
				Hospitality & Tourism Management
			  </p>

			  <div className="mt-4 space-y-1 text-sm text-gray-600">
				
				<p>Contact the University</p>
			  </div>
			</div>
		  </div>
		</div>


        <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-100">

          <Quote
            size={42}
            className="text-[#d99b16]"
          />

          <p className="mt-5 text-lg leading-9 text-slate-600">

            Welcome to the Department of Hospitality & Tourism Management, Bandarban University.
			<br/>The Department of Hospitality & Tourism Management is committed to quality education, practical learning, innovation and research. Our goal is to prepare graduates with professional knowledge, service skills and ethical values for the evolving hospitality and tourism industry.

			<br/><br/>Through effective teaching, practical demonstrations, field visits, internships, collaborative projects and research, we seek to foster creativity, critical thinking, leadership and responsible tourism practices.

			<br/><br/>We believe that hospitality and tourism education combines theory with practice. The Department aspires to nurture competent hospitality professionals, tourism planners, entrepreneurs and researchers who can contribute to Bangladesh and the global tourism community.

          </p>

          <div className="mt-8 border-t border-slate-100 pt-6">

            <h3 className="font-bold text-[#07552e]">
              Chairman
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Department of Hospitality & Tourism Management
            </p>

          </div>

        </div>

      </div>

    </div>

  );
}


/* =========================================================
   TUITION FEES
========================================================= */

function TuitionFees() {

  return (

    <div>

      <SectionHeader
        eyebrow="Fees"
        title="Tuition & Fee Structure"
        description="Hospitality & Tourism Management Programme"
      />


      <div className="overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">

        <div className="overflow-x-auto">

          <table className="w-full min-w-[1100px] border-collapse">

            <thead>

              <tr className="bg-[#07552e] text-left text-sm text-white">

                <th className="px-5 py-5">
                  Program
                </th>

                <th className="px-5 py-5">
                  Duration
                </th>

                <th className="px-5 py-5">
                  Semester
                </th>

                <th className="px-5 py-5">
                  Credits
                </th>

                <th className="px-5 py-5">
                  Admission Fee
                </th>

                <th className="px-5 py-5">
                  Semester Fee
                </th>

                <th className="px-5 py-5">
                  Establishement Fee
                </th>

                <th className="px-5 py-5">
                  Library Fee
                </th>

                <th className="px-5 py-5">
                  Lab Fee/Semester
                </th>

                <th className="px-5 py-5">
                  Exam Fee/Credit
                </th>

                <th className="px-5 py-5">
                  Tuition/Credit
                </th>

                <th className="px-5 py-5">
                  Total
                </th>

              </tr>

            </thead>


            <tbody>

              <tr className="border-b border-slate-100">

                <td className="px-5 py-6 font-bold text-[#07552e]">

                 Bachelor of Hospitality and Tourism Management

                </td>

                <td className="px-5 py-6">
                  4 Years	
                </td>

                <td className="px-5 py-6">
                  8
                </td>

                <td className="px-5 py-6">

                  141  

                </td>

                <td className="px-5 py-6">
                  ৳12,000
                </td>

                <td className="px-5 py-6">
                  ৳5,000
                </td>

                <td className="px-5 py-6">
                  ৳2,000
                </td>

                <td className="px-5 py-6">
                  ৳1,000
                </td>

                <td className="px-5 py-6">
                  ৳500 
                </td>

                <td className="px-5 py-6">
                  ৳100 
                </td>

                <td className="px-5 py-6">
                 ৳1,910 
                </td>

                <td className="px-5 py-6 text-lg font-extrabold text-[#07552e]">
                  ৳3,42,410 BDT
                </td>

              </tr>

            </tbody>

          </table>


        </div>

      </div>

    </div>

  );
}


/* =========================================================
   NOTICE
========================================================= */

function NoticeSection({
  notices,
  loading,
}) {

  return (

    <div>

      <SectionHeader
        eyebrow="Department Notice"
        title="Latest Notices"
        description="Important academic and administrative announcements."
      />


      {loading ? (

        <Loading />

      ) : notices.length === 0 ? (

        <EmptyState text="No notice available." />

      ) : (

        <div className="grid gap-5 md:grid-cols-2">

          {notices.map((notice, index) => (

            <motion.div
              key={notice.id || notice.notice_id || index}
              whileHover={{ y: -5 }}
              className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100"
            >

              <div className="flex gap-4">

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-50 text-[#07552e]">

                  <FileText size={22} />

                </div>


                <div className="min-w-0">

                  <h3 className="font-bold text-slate-800">

                    {notice.head_line ||
                      notice.notice_title ||
                      notice.subject ||
                      "Department Notice"}

                  </h3>


                  <p className="mt-2 text-sm text-slate-500">

                    {notice.date ||
                      notice.notice_date ||
                      notice.created_at ||
                      ""}

                  </p>


                  {notice.description && (

                    <p className="mt-3 line-clamp-2 text-sm leading-6 text-slate-500">
                      {notice.description}
                    </p>

                  )}

                </div>

              </div>

            </motion.div>

          ))}

        </div>

      )}

    </div>

  );
}


/* =========================================================
   NEWS
========================================================= */

function NewsSection({
  news,
  loading,
}) {

  return (

    <div>

      <SectionHeader
        eyebrow="Department News"
        title="Latest News & Updates"
        description="Stay connected with the latest activities and achievements."
      />


      {loading ? (

        <Loading />

      ) : news.length === 0 ? (

        <EmptyState text="No news available." />

      ) : (

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">

          {news.map((item, index) => {

            const image =
              item.images_path ||
              item.picture ||
              item.news_image ||
              item.photo;

            return (

              <motion.article
                key={item.id || item.news_id || index}
                whileHover={{
                  y: -7,
                }}
                className="group overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100"
              >

                <div className="relative h-52 overflow-hidden bg-green-50">

                  {image ? (

                    <img
                      src={`${import.meta.env.VITE_API_SOURCE_URL}/${image}`}
                      alt=""
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />

                  ) : (

                    <div className="flex h-full items-center justify-center">

                      <Newspaper
                        size={60}
                        className="text-[#07552e]/30"
                      />

                    </div>

                  )}

                  <div className="absolute left-4 top-4 rounded-full bg-[#07552e] px-3 py-1 text-xs font-bold text-white">

                    NEWS

                  </div>

                </div>


                <div className="p-6">

                  <p className="text-xs font-semibold text-[#b47c00]">

                    {item.date ||
                      item.news_date ||
                      item.created_at ||
                      ""}

                  </p>


                  <h3 className="mt-3 line-clamp-2 text-xl font-bold text-slate-800">

                    {item.head_line ||
                      item.news_title ||
                      item.subject ||
                      "Department News"}

                  </h3>


                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-500">

                    {item.description ||
                      item.short_description ||
                      ""}

                  </p>


                  <a href={`/news/${item.id}`} className="mt-5 flex items-center gap-2 text-sm font-bold text-[#07552e]">

                    Read More

                    <ArrowRight size={16} />

                  </a>

                </div>

              </motion.article>

            );

          })}

        </div>

      )}

    </div>

  );
}


/* =========================================================
   FACULTY
========================================================= */

function FacultySection({
  faculty,
  loading,
}) {

  return (

    <div>

      <SectionHeader
        eyebrow="Our People"
        title="Faculty Members"
        description="Meet the dedicated faculty members of the Department of Hospitality & Tourism Management."
      />


      {loading ? (

        <Loading />

      ) : faculty.length === 0 ? (

        <EmptyState text="No faculty information available." />

      ) : (

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

          {faculty.map((teacher, index) => {

            const picture =
              teacher.picture ||
              teacher.photo ||
              teacher.image;

            return (

              <motion.div
                key={teacher.teacher_id || index}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: index * 0.05,
                }}
                whileHover={{
                  y: -7,
                }}
                className="group overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100"
              >

                <div className="relative h-64 overflow-hidden bg-gradient-to-br from-green-50 to-amber-50">

                  {picture ? (

                    <img
                      src={`${import.meta.env.VITE_API_SOURCE_URL}/${picture}`}
                      alt={teacher.name}
                      className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                    />

                  ) : (

                    <div className="flex h-full items-center justify-center">

                      <Users
                        size={80}
                        strokeWidth={1}
                        className="text-[#07552e]/30"
                      />

                    </div>

                  )}

                </div>


                <div className="p-5">

                  <h3 className="text-lg font-bold text-slate-800">

                    {teacher.name ||
                      teacher.teacher_name ||
                      "Faculty Member"}

                  </h3>


                  <p className="mt-1 text-sm font-semibold text-[#07552e]">

                    {teacher.designation_name ||
                      teacher.designation ||
                      ""}

                  </p>


                  <p className="mt-1 text-xs text-slate-500">

                    {teacher.department_name || ""}

                  </p>


                  <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">

                    {teacher.email && (

                      <p className="flex items-center gap-2 truncate text-xs text-slate-500">

                        <Mail size={14} />

                        {teacher.email}

                      </p>

                    )}

                    {teacher.mobile_number && (

                      <p className="flex items-center gap-2 text-xs text-slate-500">

                        <Phone size={14} />

                        {teacher.mobile_number}

                      </p>

                    )}

                  </div>


                  <button
                    onClick={() => {
                      window.location.href =
                        `/profile/${teacher.teacher_id}`;
                    }}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#07552e] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#063d24]"
                  >

                    View Profile

                    <ArrowRight size={16} />

                  </button>

                </div>

              </motion.div>

            );

          })}

        </div>

      )}

    </div>

  );
}


/* =========================================================
   CURRICULUM
========================================================= */

function Curriculum({ courses, loading }) {

  /*
   * API Response:
   *
   * {
   *   "Semester Number: 1": [...],
   *   "Semester Number: 2": [...],
   *   ...
   * }
   *
   * Convert object to array:
   *
   * Object.entries(courses)
   */

  const semesters =
    courses && typeof courses === "object" && !Array.isArray(courses)
      ? Object.entries(courses)
      : [];

  // Calculate total credits
  const totalCredits = semesters.reduce((total, [, courseList]) => {
    return (
      total +
      courseList.reduce(
        (sum, course) =>
          sum + Number(course.credit_hours || 0),
        0
      )
    );
  }, 0);

  return (
    <div>

      <SectionHeader
        eyebrow="Academic Structure"
        title="Course Curriculum"
        description="Hospitality & Tourism Management Programme"
      />

      {/* =====================================================
          PROGRAM SUMMARY
      ===================================================== */}

      {!loading && semesters.length > 0 && (
        <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">

            <div className="flex items-center gap-3">

              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 text-[#07552e]">
                <CalendarDays size={22} />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Duration
                </p>

                <p className="text-xl font-extrabold text-[#07552e]">
                  {semesters.length} Semesters
                </p>
              </div>

            </div>

          </div>


          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">

            <div className="flex items-center gap-3">

              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-[#b47c00]">
                <BookOpen size={22} />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Courses
                </p>

                <p className="text-xl font-extrabold text-[#07552e]">
                  {semesters.reduce(
                    (total, [, list]) =>
                      total + list.length,
                    0
                  )}
                </p>
              </div>

            </div>

          </div>


          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">

            <div className="flex items-center gap-3">

              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 text-[#07552e]">
                <GraduationCap size={22} />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Credits
                </p>

                <p className="text-xl font-extrabold text-[#07552e]">
                  {totalCredits}
                </p>
              </div>

            </div>

          </div>


          <div className="rounded-2xl bg-[#07552e] p-5 text-white shadow-sm">

            <div className="flex items-center gap-3">

              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-[#e8b637]">
                <Trophy size={22} />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-white/50">
                  Program
                </p>

                <p className="text-lg font-extrabold">
                  HTM
                </p>
              </div>

            </div>

          </div>

        </div>
      )}


      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading && <Loading />}


      {/* =====================================================
          NO DATA
      ===================================================== */}

      {!loading && semesters.length === 0 && (
        <EmptyState
          text="Curriculum information is not available."
        />
      )}


      {/* =====================================================
          SEMESTERS
      ===================================================== */}

      {!loading && semesters.length > 0 && (

        <div className="space-y-8">

          {semesters.map(
            ([semesterName, courseList], semesterIndex) => {

              const semesterCredits = courseList.reduce(
                (sum, course) =>
                  sum + Number(course.credit_hours || 0),
                0
              );

              return (

                <motion.div
                  key={semesterName}
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    delay: semesterIndex * 0.08,
                  }}
                  className="overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100"
                >

                  {/* =========================================
                      SEMESTER HEADER
                  ========================================= */}

                  <div className="flex flex-col gap-4 bg-gradient-to-r from-[#07552e] to-[#08733b] px-6 py-5 text-white sm:flex-row sm:items-center sm:justify-between">

                    <div className="flex items-center gap-4">

                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 font-extrabold text-[#e8b637]">

                        {semesterIndex + 1}

                      </div>

                      <div>

                        <h3 className="text-xl font-extrabold">
                          {semesterName}
                        </h3>

                        <p className="mt-1 text-sm text-white/60">
                          {courseList.length} Courses
                        </p>

                      </div>

                    </div>


                    <div className="flex items-center gap-2">

                      <span className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold">

                        {semesterCredits} Credits

                      </span>

                    </div>

                  </div>


                  {/* =========================================
                      COURSE TABLE
                  ========================================= */}

                  <div className="overflow-x-auto">

                    <table className="w-full min-w-[700px]">

                      <thead>

                        <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">

                          <th className="w-20 px-6 py-4">
                            SL
                          </th>

                          <th className="w-40 px-6 py-4">
                            Course Code
                          </th>

                          <th className="px-6 py-4">
                            Course Title
                          </th>

                          <th className="w-36 px-6 py-4 text-center">
                            Credit Hours
                          </th>

                        </tr>

                      </thead>


                      <tbody>

                        {courseList.map(
                          (course, courseIndex) => (

                            <motion.tr
                              key={
                                course.course_id ||
                                `${semesterName}-${courseIndex}`
                              }
                              initial={{
                                opacity: 0,
                              }}
                              animate={{
                                opacity: 1,
                              }}
                              transition={{
                                delay:
                                  semesterIndex * 0.05 +
                                  courseIndex * 0.03,
                              }}
                              className="border-b border-slate-50 transition hover:bg-green-50/60"
                            >

                              {/* SL */}

                              <td className="px-6 py-5">

                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 text-sm font-bold text-[#07552e]">

                                  {course.sl ||
                                    courseIndex + 1}

                                </div>

                              </td>


                              {/* COURSE CODE */}

                              <td className="px-6 py-5">

                                <span className="rounded-lg bg-[#07552e]/5 px-3 py-2 font-mono text-sm font-bold text-[#07552e]">

                                  {course.actual_code ||
                                    course.course_code ||
                                    "-"}

                                </span>

                              </td>


                              {/* COURSE TITLE */}

                              <td className="px-6 py-5">

                                <div className="font-semibold text-slate-700">

                                  {course.course_title ||
                                    course.course_name ||
                                    course.name ||
                                    "Course"}

                                </div>

                              </td>


                              {/* CREDIT */}

                              <td className="px-6 py-5 text-center">

                                <span className="inline-flex min-w-[70px] items-center justify-center rounded-full bg-amber-50 px-4 py-2 text-sm font-extrabold text-[#9a6800]">

                                  {course.credit_hours || "0"}

                                </span>

                              </td>

                            </motion.tr>

                          )
                        )}

                      </tbody>


                      {/* SEMESTER TOTAL */}

                      <tfoot>

                        <tr className="bg-slate-50">

                          <td
                            colSpan="3"
                            className="px-6 py-4 text-right text-sm font-bold text-slate-500"
                          >

                            Semester Total

                          </td>

                          <td className="px-6 py-4 text-center">

                            <span className="font-extrabold text-[#07552e]">

                              {semesterCredits}

                            </span>

                          </td>

                        </tr>

                      </tfoot>

                    </table>

                  </div>

                </motion.div>

              );
            }
          )}

        </div>

      )}

    </div>
  );
}

/* =========================================================
   COURSE OFFERED
========================================================= */

function CourseOffered({
  courses,
  loading,
}) {

  const semesters =
    courses &&
    typeof courses === "object" &&
    !Array.isArray(courses)
      ? Object.entries(courses)
      : [];


  const totalCourses = semesters.reduce(
    (total, [, list]) =>
      total + list.length,
    0
  );


  const totalCredits = semesters.reduce(
    (total, [, list]) =>
      total +
      list.reduce(
        (sum, course) =>
          sum + Number(course.credit_hours || 0),
        0
      ),
    0
  );


  return (

    <div>

      <SectionHeader
        eyebrow="Current Academic Offering"
        title="Course Offered"
        description="Courses offered by the Department of Hospitality & Tourism Management."
      />


      {/* =====================================================
          SUMMARY
      ===================================================== */}

      {!loading && semesters.length > 0 && (

        <div className="mb-8 grid gap-4 sm:grid-cols-3">

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100">

            <div className="flex items-center gap-4">

              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 text-[#07552e]">

                <CalendarDays size={22} />

              </div>

              <div>

                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Semesters
                </p>

                <p className="text-2xl font-extrabold text-[#07552e]">

                  {semesters.length}

                </p>

              </div>

            </div>

          </div>


          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100">

            <div className="flex items-center gap-4">

              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-[#b47c00]">

                <BookOpen size={22} />

              </div>

              <div>

                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Courses
                </p>

                <p className="text-2xl font-extrabold text-[#07552e]">

                  {totalCourses}

                </p>

              </div>

            </div>

          </div>


          <div className="rounded-2xl bg-[#07552e] p-6 text-white shadow-sm">

            <div className="flex items-center gap-4">

              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-[#e8b637]">

                <GraduationCap size={22} />

              </div>

              <div>

                <p className="text-xs font-semibold uppercase tracking-wider text-white/50">
                  Total Credits
                </p>

                <p className="text-2xl font-extrabold">

                  {totalCredits}

                </p>

              </div>

            </div>

          </div>

        </div>

      )}


      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading && <Loading />}


      {/* =====================================================
          EMPTY
      ===================================================== */}

      {!loading && semesters.length === 0 && (

        <EmptyState
          text="No course offering is available."
        />

      )}


      {/* =====================================================
          SEMESTER CARDS
      ===================================================== */}

      {!loading && semesters.length > 0 && (

        <div className="grid gap-6 lg:grid-cols-2">

          {semesters.map(
            ([semesterName, courseList], index) => {

              const semesterCredits =
                courseList.reduce(
                  (sum, course) =>
                    sum +
                    Number(
                      course.credit_hours || 0
                    ),
                  0
                );


              return (

                <motion.div
                  key={semesterName}
                  initial={{
                    opacity: 0,
                    scale: 0.97,
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                  }}
                  transition={{
                    delay: index * 0.08,
                  }}
                  whileHover={{
                    y: -5,
                  }}
                  className="overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100"
                >

                  {/* HEADER */}

                  <div className="flex items-center justify-between bg-gradient-to-r from-[#07552e] to-[#08733b] px-6 py-5 text-white">

                    <div className="flex items-center gap-3">

                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#e8b637]">

                        <BookOpen size={21} />

                      </div>

                      <div>

                        <h3 className="font-extrabold">
                          {semesterName}
                        </h3>

                        <p className="mt-1 text-xs text-white/60">

                          {courseList.length} Courses

                        </p>

                      </div>

                    </div>


                    <span className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold">

                      {semesterCredits} Credits

                    </span>

                  </div>


                  {/* COURSES */}

                  <div className="divide-y divide-slate-100">

                    {courseList.map(
                      (course, courseIndex) => (

                        <div
                          key={
                            course.course_id ||
                            `${semesterName}-${courseIndex}`
                          }
                          className="flex items-center gap-4 p-5 transition hover:bg-green-50/50"
                        >

                          {/* NUMBER */}

                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-50 text-xs font-bold text-[#07552e]">

                            {course.sl ||
                              courseIndex + 1}

                          </div>


                          {/* CONTENT */}

                          <div className="min-w-0 flex-1">

                            <div className="flex flex-wrap items-center gap-2">

                              <span className="font-mono text-xs font-bold text-[#07552e]">

                                {course.actual_code ||
                                  course.course_code ||
                                  "-"}

                              </span>

                            </div>

                            <h4 className="mt-1 font-semibold text-slate-700">

                              {course.course_title ||
                                course.course_name ||
                                course.name ||
                                "Course"}

                            </h4>

                          </div>


                          {/* CREDIT */}

                          <span className="shrink-0 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-extrabold text-[#9a6800]">

                            {course.credit_hours || "0"} Cr

                          </span>

                        </div>

                      )
                    )}

                  </div>


                  {/* FOOTER */}

                  <div className="flex items-center justify-between bg-slate-50 px-6 py-4">

                    <span className="text-xs font-semibold text-slate-500">
                      Semester Total
                    </span>

                    <span className="text-sm font-extrabold text-[#07552e]">

                      {semesterCredits} Credits

                    </span>

                  </div>

                </motion.div>

              );
            }
          )}

        </div>

      )}

    </div>

  );
}


/* =========================================================
   EVENTS
========================================================= */

function EventsSection({
  events,
  loading,
}) {

  return (

    <div>

      <SectionHeader
        eyebrow="Department Activities"
        title="Events"
        description="Stay updated with seminars, workshops, tourism and hospitality competitions and other activities of the Department of Hospitality & Tourism Management."
      />


      {loading ? (

        <Loading />

      ) : events.length === 0 ? (

        <EmptyState
          text="No department events available."
        />

      ) : (

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">

          {events.map((event, index) => {

            const eventImage =
              event.images ||
              event.picture ||
              event.event_image ||
              event.photo ||
              event.thumbnail;


            const eventTitle =
              event.name ||
              event.event_title ||
              event.name ||
              "Department Event";


            const eventDate =
              event.start_date ||
              event.event_date ||
              event.start_date ||
              event.created_at ||
              "";


            const eventDescription =
              event.description ||
              event.short_description ||
              event.details ||
              "";


            return (

              <motion.article
                key={
                  event.id ||
                  event.event_id ||
                  index
                }
                initial={{
                  opacity: 0,
                  y: 25,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: index * 0.06,
                }}
                whileHover={{
                  y: -7,
                }}
                className="group overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100"
              >

                {/* =========================================
                    IMAGE
                ========================================= */}

                <div className="relative h-56 overflow-hidden bg-gradient-to-br from-green-50 to-amber-50">

                  {eventImage ? (

                    <img
                      src={`${import.meta.env.VITE_API_SOURCE_URL}/${eventImage}`}
                      alt={eventTitle}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />

                  ) : (

                    <div className="flex h-full items-center justify-center">

                      <CalendarDays
                        size={70}
                        strokeWidth={1}
                        className="text-[#07552e]/25"
                      />

                    </div>

                  )}


                  {/* Event Label */}

                  <div className="absolute left-4 top-4">

                    <span className="rounded-full bg-[#07552e] px-4 py-2 text-xs font-bold text-white shadow-lg">

                      EVENT

                    </span>

                  </div>


                  {/* Date */}

                  {eventDate && (

                    <div className="absolute bottom-4 left-4">

                      <div className="flex items-center gap-2 rounded-xl bg-white/95 px-3 py-2 text-xs font-bold text-[#07552e] shadow-lg backdrop-blur">

                        <CalendarDays size={15} />

                        {eventDate}

                      </div>

                    </div>

                  )}

                </div>


                {/* =========================================
                    CONTENT
                ========================================= */}

                <div className="p-6">

                  <h3 className="line-clamp-2 text-xl font-bold text-slate-800 transition group-hover:text-[#07552e]">

                    {eventTitle}

                  </h3>


                  {eventDescription && (

                    <p className="mt-3 line-clamp-3 text-sm leading-7 text-slate-500">

                      {eventDescription}

                    </p>

                  )}


                  <button
                    type="button"
                    className="mt-5 flex items-center gap-2 text-sm font-bold text-[#07552e] transition group-hover:gap-3"
                  >

                    View Event

                    <ArrowRight size={16} />

                  </button>

                </div>

              </motion.article>

            );

          })}

        </div>

      )}

    </div>

  );
}

/* =========================================================
   VIDEOS
========================================================= */

function VideosSection({
  videos,
  selectedVideo,
  setSelectedVideo,
  autoPlay,
  setAutoPlay,
  loading,
}) {

  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };


  const getYouTubeEmbed = (videoId) => {

    return `https://www.youtube.com/embed/${videoId}${
      autoPlay ? "?autoplay=1" : ""
    }`;

  };


  const selectVideo = (video) => {

    setSelectedVideo(video);

    setAutoPlay(true);

  };


  return (

    <div>

      <SectionHeader
        eyebrow="Media & Activities"
        title="Department Videos"
        description="Watch seminars, workshops, tourism and hospitality competitions and other activities of the Department of Hospitality & Tourism Management."
      />


      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading && (

        <Loading />

      )}


      {/* =====================================================
          EMPTY
      ===================================================== */}

      {!loading && videos.length === 0 && (

        <EmptyState
          text="No department videos available."
        />

      )}


      {!loading && videos.length > 0 && (

        <div className="grid gap-8 lg:grid-cols-[1.35fr_.65fr]">


          {/* =================================================
              SELECTED VIDEO
          ================================================= */}

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.97,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            className="overflow-hidden rounded-3xl bg-[#063d24] shadow-xl"
          >

            {/* YouTube Player */}

            <div className="relative aspect-video w-full overflow-hidden bg-black">

              {selectedVideo && (

                <iframe
                  key={`${selectedVideo.video_id}-${autoPlay}`}
                  src={getYouTubeEmbed(
                    selectedVideo.video_id
                  )}
                  title={
                    selectedVideo.title ||
                    "Department Video"
                  }
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />

              )}

            </div>


            {/* Video Information */}

            {selectedVideo && (

              <div className="p-6 md:p-7">

                <div className="flex flex-wrap items-center gap-3">

                  <span className="rounded-full bg-[#d99b16] px-3 py-1.5 text-xs font-bold text-white">

                    DEPARTMENT VIDEO

                  </span>


                  {selectedVideo.date && (

                    <span className="flex items-center gap-1.5 text-xs text-white/60">

                      <CalendarDays size={14} />

                      {selectedVideo.date}

                    </span>

                  )}

                </div>


                <h3 className="mt-4 text-xl font-extrabold leading-8 text-white md:text-2xl">

                  {selectedVideo.title ||
                    "Department Video"}

                </h3>


                <div className="mt-5 flex items-center justify-between">

                  <p className="text-xs text-white/50">

                    Hospitality & Tourism Management

                  </p>


                  <a
                    href={`https://www.youtube.com/watch?v=${selectedVideo.video_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-xs font-bold text-white transition hover:bg-white/20"
                  >

                    Watch on YouTube

                    <ArrowRight size={14} />

                  </a>

                </div>

              </div>

            )}

          </motion.div>


          {/* =================================================
              VIDEO LIST
          ================================================= */}

          <div className="flex max-h-[650px] flex-col overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100">

            {/* List Header */}

            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">

              <div>

                <h3 className="font-bold text-[#07552e]">
                  Latest Videos
                </h3>

                <p className="mt-1 text-xs text-slate-400">

                  {videos.length} videos available

                </p>

              </div>


              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-50 text-[#07552e]">

                <Video size={18} />

              </div>

            </div>


            {/* Scrollable List */}

            <div className="overflow-y-auto">

              {videos.map((video, index) => {

                const active =
                  selectedVideo?.video_id ===
                  video.video_id;


                return (

                  <motion.button
                    key={
                      video.video_id ||
                      index
                    }
                    onClick={() =>
                      selectVideo(video)
                    }
                    whileHover={{
                      x: 3,
                    }}
                    className={`
                      group
                      flex
                      w-full
                      gap-3
                      border-b
                      border-slate-100
                      p-4
                      text-left
                      transition-all

                      ${
                        active
                          ? "bg-green-50"
                          : "bg-white hover:bg-slate-50"
                      }
                    `}
                  >

                    {/* Thumbnail */}

                    <div className="relative h-[75px] w-[110px] shrink-0 overflow-hidden rounded-xl bg-slate-100">

                      <img
                        src={getYouTubeThumbnail(
                          video.video_id
                        )}
                        alt={
                          video.title ||
                          "Video"
                        }
                        className={`
                          h-full
                          w-full
                          object-cover
                          transition
                          duration-500
                          ${
                            active
                              ? "scale-105"
                              : "group-hover:scale-105"
                          }
                        `}
                      />


                      {/* Play Icon */}

                      <div
                        className={`
                          absolute
                          inset-0
                          flex
                          items-center
                          justify-center
                          ${
                            active
                              ? "bg-[#07552e]/30"
                              : "bg-black/20"
                          }
                        `}
                      >

                        <div
                          className={`
                            flex
                            h-8
                            w-8
                            items-center
                            justify-center
                            rounded-full
                            ${
                              active
                                ? "bg-[#d99b16] text-white"
                                : "bg-white/90 text-[#07552e]"
                            }
                          `}
                        >

                          <Play
                            size={14}
                            fill="currentColor"
                          />

                        </div>

                      </div>


                      {/* Number */}

                      <span className="absolute left-2 top-2 flex h-5 min-w-5 items-center justify-center rounded-md bg-black/60 px-1 text-[10px] font-bold text-white">

                        {index + 1}

                      </span>

                    </div>


                    {/* Information */}

                    <div className="min-w-0 flex-1">

                      <h4
                        className={`
                          line-clamp-2
                          text-sm
                          font-bold
                          leading-5

                          ${
                            active
                              ? "text-[#07552e]"
                              : "text-slate-700"
                          }
                        `}
                      >

                        {video.title ||
                          "Department Video"}

                      </h4>


                      {video.date && (

                        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">

                          <CalendarDays size={12} />

                          {video.date}

                        </p>

                      )}

                    </div>

                  </motion.button>

                );

              })}

            </div>

          </div>

        </div>

      )}

    </div>

  );
}


/* =========================================================
   FINANCIAL AID
========================================================= */

function FinancialAid() {

  const items = [
    "Merit-based scholarships",
    "Need-based financial assistance",
    "University scholarships",
    "Special financial assistance",
    "Academic achievement awards",
  ];


  return (

    <div>

      <SectionHeader
        eyebrow="Student Support"
        title="Financial Aid"
        description="The university provides various opportunities to support deserving and meritorious students."
      />


      <div className="grid gap-8 lg:grid-cols-2">

        <div className="rounded-3xl bg-gradient-to-br from-[#07552e] to-[#063d24] p-8 text-white">

          <HeartHandshake
            size={48}
            className="text-[#e8b637]"
          />

          <h3 className="mt-6 text-2xl font-bold">
            Supporting Student Success
          </h3>

          <p className="mt-4 leading-8 text-white/70">

            Financial support opportunities are designed to help
            talented and deserving students continue their education
            and achieve their academic goals.

          </p>

        </div>


        <div className="space-y-3">

          {items.map((item) => (

            <div
              key={item}
              className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100"
            >

              <CheckCircle2
                size={22}
                className="text-[#08733b]"
              />

              <span className="font-semibold text-slate-700">
                {item}
              </span>

            </div>

          ))}

        </div>

      </div>

    </div>

  );
}


/* =========================================================
   BUSINESS CLUB
========================================================= */

function HTMClub() {

  return (

    <div>

      <SectionHeader
        eyebrow="Student Life"
        title="HTM Club"
        description="A platform for students to develop hospitality and tourism knowledge, teamwork, entrepreneurship, leadership and professional skills."
      />


      <div className="grid gap-8 lg:grid-cols-3">

        {[
          {
            icon: Users,
            title: "Hospitality Case Studies",
            text: "Develop analytical and decision-making skills through real-world hospitality and tourism case studies and collaborative learning.",
          },
          {
            icon: Trophy,
            title: "Tourism & Hospitality Competitions",
            text: "Participate in tourism promotion, hospitality service and case competitions, presentations and entrepreneurial challenges.",
          },
          {
            icon: HeartHandshake,
            title: "Innovation & Community",
            text: "Collaborate on tourism initiatives, cultural programmes and community projects that address real-world hospitality and destination needs.",
          },
        ].map((item) => {

          const Icon = item.icon;

          return (

            <motion.div
              key={item.title}
              whileHover={{ y: -7 }}
              className="rounded-3xl bg-white p-7 text-center shadow-sm ring-1 ring-slate-100"
            >

              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-50 text-[#07552e]">

                <Icon size={30} />

              </div>

              <h3 className="mt-6 text-xl font-bold">
                {item.title}
              </h3>

              <p className="mt-3 text-sm leading-7 text-slate-500">
                {item.text}
              </p>

            </motion.div>

          );

        })}

      </div>

    </div>

  );
}


/* =========================================================
   RESEARCH
========================================================= */

function Research() {

  return (

    <div>

      <SectionHeader
        eyebrow="Academic Excellence"
        title="Research"
        description="Exploring research and innovation in hospitality operations, tourism management, destination development and service innovation."
      />


      <div className="grid gap-6 md:grid-cols-2">

        {[
          "Hospitality Operations and Service Management",
          "Tourism Marketing and Consumer Behaviour",
          "Hospitality Finance and Revenue Management",
          "Human Resource Management in Hospitality",
          "Tourism Entrepreneurship and Small Business Development",
          "Sustainable Tourism and Destination Management",
        ].map((item) => (

          <motion.div
            key={item}
            whileHover={{ x: 5 }}
            className="flex items-center gap-4 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100"
          >

            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 text-[#07552e]">

              <FlaskConical size={22} />

            </div>

            <span className="font-bold text-slate-700">
              {item}
            </span>

          </motion.div>

        ))}

      </div>

    </div>

  );
}


/* =========================================================
   FACILITIES
========================================================= */

function Facilities() {

  const facilities = [
    {
      icon: Building2,
      title: "Modern Classrooms",
      text: "Comfortable learning environment equipped for interactive hospitality and tourism education.",
    },
    {
      icon: BookOpen,
      title: "Library",
      text: "Access to hospitality and tourism books, journals, case studies and digital learning resources.",
    },
    {
      icon: FlaskConical,
      title: "Research Facilities",
      text: "Support for hospitality and tourism research, case analysis and student projects.",
    },
    {
      icon: Users,
      title: "Student Activities",
      text: "Opportunities for HTM club activities, seminars, tourism competitions and professional development.",
    },
  ];


  return (

    <div>

      <SectionHeader
        eyebrow="Campus Life"
        title="Department Facilities"
      />


      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">

        {facilities.map((item) => {

          const Icon = item.icon;

          return (

            <motion.div
              key={item.title}
              whileHover={{ y: -7 }}
              className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100"
            >

              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 text-[#07552e]">

                <Icon size={26} />

              </div>

              <h3 className="mt-5 font-bold">
                {item.title}
              </h3>

              <p className="mt-3 text-sm leading-7 text-slate-500">
                {item.text}
              </p>

            </motion.div>

          );

        })}

      </div>

    </div>

  );
}


/* =========================================================
   CONTACT
========================================================= */

function Contact() {

  return (

    <div>

      <SectionHeader
        eyebrow="Get in Touch"
        title="Contact Us"
        description="For academic, admission and departmental information, please contact us."
      />


      <div className="grid gap-8 lg:grid-cols-2">

        <div className="rounded-3xl bg-[#07552e] p-8 text-white">

          <h3 className="text-2xl font-bold">
            Department of Hospitality & Tourism Management
          </h3>


          <div className="mt-8 space-y-6">

            <InfoRow
              icon={MapPin}
              title="Address"
              value="Bandarban University, Bandarban, Bangladesh"
            />

            <InfoRow
              icon={Phone}
              title="Phone"
              value="Please contact the University office"
            />

            <InfoRow
              icon={Mail}
              title="Email"
              value="Please contact the University office"
            />

          </div>

        </div>


        <div className="flex min-h-[350px] items-center justify-center rounded-3xl bg-gradient-to-br from-green-50 to-amber-50">

          <iframe title="Google Map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14783.37626274081!2d92.15118527412412!3d22.131920330993385!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30ad6b2819643f39%3A0x769c49f9d228c2a9!2sBandarban%20University%20Campus!5e0!3m2!1sen!2sbd!4v1776353218504!5m2!1sen!2sbd" className="h-full w-full border-0" loading="lazy"></iframe>

        </div>

      </div>

    </div>

  );
}


/* =========================================================
   INFO ROW
========================================================= */

function InfoRow({
  icon: Icon,
  title,
  value,
}) {

  return (

    <div className="flex gap-4">

      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10">

        <Icon size={20} />

      </div>

      <div>

        <p className="text-xs uppercase tracking-wider text-white/50">
          {title}
        </p>

        <p className="mt-1 font-semibold">
          {value}
        </p>

      </div>

    </div>

  );
}


/* =========================================================
   LOADING
========================================================= */

function Loading() {

  return (

    <div className="flex min-h-[250px] items-center justify-center">

      <div className="text-center">

        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-green-100 border-t-[#07552e]" />

        <p className="mt-4 text-sm text-slate-500">
          Loading information...
        </p>

      </div>

    </div>

  );
}


/* =========================================================
   EMPTY STATE
========================================================= */

function EmptyState({
  text,
}) {

  return (

    <div className="rounded-3xl bg-white py-20 text-center shadow-sm ring-1 ring-slate-100">

      <FileText
        size={50}
        className="mx-auto text-slate-200"
      />

      <p className="mt-5 text-slate-500">
        {text}
      </p>

    </div>

  );
}